import { User } from "firebase/auth";
import React, { useEffect, useRef, useState, useCallback } from "react";
import PageLoader from "../components/PageLoader";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { usePreviewMode } from "../context/PreviewModeContext";
import { fetchLeague, deleteLeague, addGuest, GwSummary } from "../api/leagues";
import { getGwChallengePicks, GwChallengePickRow } from "../api/gwChallenge";
import { getSyndicatePicks, SyndicatePicksResponse } from "../api/syndicate";
import ConfirmModal from "../components/ConfirmModal";
import StatBox from "../components/league/StatBox";
import FormBar, { FormBarEntry } from "../components/league/FormBar";
import LeagueProgressBar from "../components/league/LeagueProgressBar";
import HowItWorksPanel from "../components/league/HowItWorksPanel";
import GwSummaryPanel from "../components/league/GwSummaryPanel";
import TrophiesPanel from "../components/league/TrophiesPanel";
import FixturesPanel from "../components/league/FixturesPanel";
import GwScorersPanel from "../components/league/GwScorersPanel";
import SyndicateGwStatsPanel from "../components/league/SyndicateGwStatsPanel";
import ModeIconSmall from "../components/league/ModeIconSmall";
import { computeTrophies } from "../lib/trophies";
import { computeTargetPace } from "../lib/targetPace";
import { computePositionBands } from "../lib/fpBands";
import { fpBandAccent, FPBandPill } from "../components/league/FPBandDivider";
import { MODE_CONTENT, MODE_COLOR_VAR } from "./createLeague/modeContent";
import HowToPlayModal, { LeagueFact } from "../components/HowToPlayModal";
import GameweekRecapModal from "../components/league/GameweekRecapModal";
import { hasSeenGwRecap } from "../lib/gwRecapSeen";
import StatusDot from "../components/StatusDot";
import ClubBadge from "../components/ClubBadge";
import { getClubBadge } from "../data/clubBadges";
import { getFlagUrl } from "../data/nationalityFlags";
import { useExportImage } from "../hooks/useExportImage";
import { safeFilename } from "../utils/shareLeague";
import { buildJoinUrl } from "../lib/inviteLink";
import {
  card,
  widePageContainer,
  primaryBtn,
  secondaryBtn,
  dangerBtn,
  inputStyle,
  panelHeading,
} from "../styles";
import { useIsMobile } from "../hooks/useIsMobile";
import Modal from "../components/Modal";
import {
  Member,
  LeagueMode,
  LeaderboardCtx,
} from "../components/league/leaderboard/types";
import Leaderboard from "../components/league/leaderboard/Leaderboard";
import { computeRanks } from "../components/league/leaderboard/sort";
import { myTeamHref } from "../components/league/leaderboard/links";

interface Props {
  user: User;
}

interface LeagueRules {
  mode: LeagueMode;
  target: number | null;
}

interface LeagueData {
  id: string;
  name: string;
  inviteCode: string;
  adminId: string;
  mode: LeagueMode;
  rules: LeagueRules;
  status: "PREPARING" | "RUNNING" | "COMPLETED";
  competition: {
    id: string;
    name: string;
    season: string;
    status: "UPCOMING" | "ACTIVE" | "COMPLETED";
    gameweeks: { id: string; number: number; name: string; shortName: string | null; completedAt: string | null }[];
  };
  startGameweek: { id: string; number: number; name: string } | null;
  activeGameweek: { id: string; number: number; name: string } | null;
  members: Member[];
  isLeagueAdmin: boolean;
  myMemberId: string | null;

  gwHighlights: { gameweek: string; memberId: string | null; displayName: string | null; score: number }[];
  gwSummary: GwSummary | null;
  // Final Positions fields
  isLocked?: boolean;
  fpClubs?: { id: string; name: string; shortName: string | null; crest: string | null }[];
  latestStandings?: { clubId: string; position: number }[];
  myPrediction?: { id: string; clubId: string; position: number }[];
  fpScoringConfig?: { topTierSize: number | null; relegationSize: number | null; championEnabled: boolean; topTierLabel: string | null; relegationLabel: string | null };
  gwcNextPickMissing?: boolean;
  spNextGwPredictionsMissing?: boolean;
  // Syndicate fields
  syndicatePotCount?: number;
  syndicateGoalsToDate?: number;
  // New dashboard fields
  rankHistory: { gwNumber: number; gwName: string; gwShortName: string; rank: number; score: number }[];
  leagueGwAverage?: number;
}



// ─── Component ────────────────────────────────────────────────────────────────
export default function LeaguePage({ user }: Props) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { previewMode } = usePreviewMode();
  const isMobile = useIsMobile();
  const exportFullRef        = useRef<HTMLDivElement>(null);
  const exportHeaderRef      = useRef<HTMLDivElement>(null);
  const exportLeaderboardRef = useRef<HTMLElement>(null);

  const [league, setLeague] = useState<LeagueData | null>(null);
  const [gwcPickRows, setGwcPickRows] = useState<GwChallengePickRow[] | null>(null);
  const [syndicatePicksData, setSyndicatePicksData] = useState<SyndicatePicksResponse | null>(null);
  useBreadcrumbs(league ? [{ label: t("common.home"), to: "/overview" }, { label: league.name }] : [{ label: t("common.home"), to: "/overview" }]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [showCreatedBanner, setShowCreatedBanner] = useState(Boolean((location.state as { justCreated?: boolean } | null)?.justCreated));

  // Header modals
  const [infoOpen, setInfoOpen]       = useState(false);
  const [gwRecapOpen, setGwRecapOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inviteOpen, setInviteOpen]   = useState(false);

  // Recheck banner/how-to-play state when league ID changes (component reuses across
  // navigation), and strip the one-off flags so refresh/back don't resurrect them.
  useEffect(() => {
    const state = location.state as { justCreated?: boolean; justJoined?: boolean } | null;
    const justCreated = Boolean(state?.justCreated);
    const justJoined = Boolean(state?.justJoined);
    setShowCreatedBanner(justCreated);
    if (justCreated || justJoined) {
      setInfoOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Guest form (inside invite modal)
  const [guestName, setGuestName]     = useState("");
  const [addingGuest, setAddingGuest] = useState(false);

  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Share
  const [shareDropdownOpen, setShareDropdownOpen] = useState(false);
  const { status: exportStatus, loading: shareLoading, runExport } = useExportImage();

  // Row expand
  const loadLeague = useCallback(async () => {
    if (!id) return;
    try {
      const data = await fetchLeague(user, id);
      const leagueData = data as unknown as LeagueData;
      setLeague(leagueData);
      if (leagueData.mode === "GAMEWEEK_CHALLENGE" && leagueData.status === "PREPARING") {
        try {
          const picks = await getGwChallengePicks(user, id);
          setGwcPickRows(picks.rows);
        } catch { /* non-fatal */ }
      }
      if (leagueData.mode === "SYNDICATE" && leagueData.myMemberId) {
        try {
          const sPicks = await getSyndicatePicks(user, id);
          setSyndicatePicksData(sPicks);
        } catch { /* non-fatal */ }
      }
    } catch {
      setError(t("league.errors.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadLeague(); }, [loadLeague]);

  // Auto-opens the gameweek recap popup on first visit after a new gameweek completes.
  // "Seen" is tracked client-side only (see lib/gwRecapSeen.ts) — this effect re-checks
  // localStorage whenever `league` reloads, so it naturally re-opens for a later gameweek
  // without needing a separate "already shown this session" guard.
  useEffect(() => {
    if (!league?.gwSummary) return;
    const me = league.members.find((m) => m.id === league.myMemberId);
    if (!me?.isReady) return;
    if (hasSeenGwRecap(user.uid, league.id, league.gwSummary.gameweekNumber)) return;
    setGwRecapOpen(true);
  }, [league, user.uid]);

  if (loading) return <PageLoader />;
  if (error) return <div style={{ padding: "2rem", color: "#c00" }}>{error}</div>;
  if (!league) return null;

  const myMember = league.members.find((m) => m.id === league.myMemberId);

  const approvedMembers = league.members.filter((m) => m.status === "APPROVED");

  // Once the league starts, members who never made any selection are "not ready"
  // and shown separately at the bottom — they don't count in the rankings.
  const isStarted = league.status !== "PREPARING";
  const readyMembers    = isStarted ? approvedMembers.filter((m) => m.isReady) : approvedMembers;
  const notReadyMembers = isStarted ? approvedMembers.filter((m) => !m.isReady) : [];

  // Respect preview mode — hide admin UI when admin is previewing as standard member
  const effectiveAdmin = league.isLeagueAdmin && !previewMode;
  const startGwNumber = league.startGameweek?.number ?? 1;
  const completedRealGwCount = league.competition.gameweeks.filter(
    (gw) => gw.number >= startGwNumber && gw.completedAt !== null
  ).length;
  const hasResults = league.status === "COMPLETED" || completedRealGwCount > 0;
  // Syndicate breaks leaderboard ties by "closest predicted total goals"
  // within the league's scoring window.
  const tiebreakerGoalsToDate = league.mode === "SYNDICATE" ? league.syndicateGoalsToDate : undefined;
  const ranks = (isStarted && hasResults) ? computeRanks(readyMembers, league.mode, tiebreakerGoalsToDate) : new Map<string, string>();

  const leaderboardCtx: LeaderboardCtx = {
    leagueId: league.id,
    myMemberId: league.myMemberId,
    mode: league.mode,
    isStarted,
    isCompleted: league.status === "COMPLETED",
    isMobile,
    tiebreakerGoalsToDate,
    gwcNextPickMissing: league.gwcNextPickMissing ?? false,
    ranks,
    showDelta: league.status !== "COMPLETED" && (
      league.mode === "TARGET" || league.mode === "GAMEWEEK_CHALLENGE" || league.mode === "SCORE_PREDICTOR"
    ),
  };

  // ── Share image ──────────────────────────────────────────────────────────────
  const handleShare = async (mode: "simple" | "full", action: "download" | "copy", withInvite = false) => {
    setShareDropdownOpen(false);
    const els = mode === "full" ? [exportFullRef.current] : [exportLeaderboardRef.current];
    // Invite-link variant is leaderboard-only (mode: "simple") — the full export is a denser,
    // more-detailed artifact, not the natural "send this to get someone to join" one.
    const captureOptions = withInvite ? { joinUrl: buildJoinUrl(league.inviteCode) } : undefined;
    await runExport(els, action, `${safeFilename(league.name)}.png`, captureOptions);
  };

  // ── Handle copy invite link ── copies a tappable URL, not the bare code: a code typed into
  // a WhatsApp message does nothing, a link does. The code itself still displays nearby as a
  // fallback for manual entry via the authenticated /leagues/join flow.
  const handleCopy = () => {
    navigator.clipboard.writeText(buildJoinUrl(league.inviteCode)).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Handle delete league ──
  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await deleteLeague(user, league.id);
      navigate("/overview");
    } catch {
      setError(t("league.errors.failedToDelete"));
      setActionLoading(false);
    }
  };

  // ── Handle add guest ──
  const handleAddGuest = async () => {
    if (!guestName.trim() || guestName.length < 2) return;
    setAddingGuest(true);
    try {
      await addGuest(user, league.id, guestName.trim());
      setGuestName("");
      setInviteOpen(false);
      loadLeague();
    } catch {
      setError(t("addGuest.errors.failed"));
    } finally {
      setAddingGuest(false);
    }
  };

  // ─── Computed helpers ─────────────────────────────────────────────────────
  const completedGwCount = completedRealGwCount;
  const totalGwCount = league.competition.gameweeks.filter((gw) => gw.number > 0).length;
  // Scoped to the league's own start gameweek, unlike totalGwCount above (which
  // LeagueProgressBar needs unscoped since it applies the startGwNumber offset itself).
  const leagueGwCount = league.competition.gameweeks.filter((gw) => gw.number >= startGwNumber).length;
  const currentGwNumber = completedGwCount;

  // Scoped to the league's own scoring window (>= startGwNumber) — a league
  // starting mid-season must never surface a pre-start GW as "results" or
  // "next up", matching the backend's own completedGws convention (e.g.
  // leagueContext.ts's scoringGws filter).
  const windowedGws = league.competition.gameweeks.filter((gw) => gw.number >= startGwNumber);
  const resultsGw = [...windowedGws].reverse().find((gw) => gw.completedAt !== null) ?? null;
  const resultsGwId = resultsGw?.id ?? null;
  const nextGwId = resultsGw
    ? (windowedGws.find((gw) => gw.number > resultsGw.number)?.id ?? null)
    : (windowedGws[0]?.id ?? null);

  const myHighlightedClubIds: Set<string> = (() => {
    if (league.mode === "SYNDICATE" && syndicatePicksData) {
      // Only CLUB-pot picks carry a clubId — PLAYER-pot picks are null here,
      // so no pot-kind check is needed before filtering.
      return new Set(syndicatePicksData.picks.map((p) => p.clubId).filter((id): id is string => id !== null));
    }
    return new Set<string>();
  })();

  // PLAYER-pot picks — the counterpart to myHighlightedClubIds, for the
  // weekly stats panel's scorer/assister/carded rows.
  const myHighlightedPlayerIds: Set<string> = syndicatePicksData
    ? new Set(syndicatePicksData.picks.map((p) => p.playerId).filter((id): id is string => id !== null))
    : new Set<string>();

  const trophies = computeTrophies({
    mode: league.mode,
    status: league.status,
    members: league.members.filter((m) => m.status === "APPROVED"),
    target: league.rules.target ?? 0,
    totalGws: leagueGwCount,
    completedGws: completedGwCount,
  });

  // ─── Stat boxes ───────────────────────────────────────────────────────────

  function renderStatBoxes() {
    if (!league) return null;
    const locked = !isStarted;
    const m = myMember;
    const scoreHistory: FormBarEntry[] = m?.gwScoreHistory ?? [];
    const rankStr = isStarted && m ? (ranks.get(m.id) ?? "—") : "—";
    const rawDelta = m?.delta ?? 0;
    const deltaStr = rawDelta > 0 ? `+${rawDelta}` : rawDelta < 0 ? `${rawDelta}` : "—";
    const deltaColor = rawDelta > 0 ? "var(--color-success)" : rawDelta < 0 ? "var(--color-danger)" : undefined;
    const deltaNode = m && rawDelta !== 0
      ? <span style={{ color: deltaColor }}>{deltaStr}</span>
      : deltaStr;

    if (league.mode === "FINAL_POSITIONS") {
      return (
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          <StatBox label={t("league.statBoxes.score")} value={m?.score ?? 0} sub={t("league.statBoxes.fp.predictionPts")} locked={locked || !m} accent borderColor="var(--color-accent)" />
          <StatBox label={t("league.statBoxes.previousGw")} value={deltaNode} locked={locked || !m} borderColor="var(--color-accent)" />
          <StatBox label={t("league.statBoxes.rank")} value={rankStr} sub={t("league.statBoxes.ofN", { n: readyMembers.length })} locked={locked || !m} mobileHide borderColor="var(--color-accent)" />
          <StatBox label={t("league.statBoxes.fp.correctPredictions")} value={m?.exactHits ?? 0} sub={t("league.statBoxes.fp.exactPositions")} locked={locked || !m} borderColor="var(--color-accent)" />
        </div>
      );
    }

    if (league.mode === "TARGET") {
      const target = league.rules.target ?? 0;
      const isBust = m?.targetState === "BUST";
      const statusLabel = m?.targetState === "BUST" ? t("league.targetState.bust") : m?.targetState === "ELIGIBLE" ? t("league.targetState.eligible") : t("league.targetState.ineligible");
      const vsTarget = m
        ? computeTargetPace({ totalGoals: m.totalGoals, target, gameweeks: league.competition.gameweeks, startGwNumber })
        : null;
      const vsTargetNode = vsTarget !== null
        ? <span>{vsTarget >= 0 ? `+${vsTarget.toFixed(1)}` : vsTarget.toFixed(1)}</span>
        : "—";
      return (
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          <StatBox label={t("league.statBoxes.target.goals")} value={m ? `${m.totalGoals}/${target}` : "—"} sub={t("league.statBoxes.target.towardTarget")} locked={locked || !m} accent borderColor="var(--color-accent)" />
          <StatBox label={t("league.statBoxes.previousGw")} value={deltaNode} locked={locked || !m} mobileHide borderColor="var(--color-accent)" />
          <StatBox label={t("league.statBoxes.target.vsTarget")} value={locked || !m ? "—" : vsTargetNode} sub={t("league.statBoxes.target.aheadBehindPace")} locked={locked || !m} borderColor="var(--color-accent)" />
          <StatBox label={t("league.statBoxes.form")} value={!locked && m ? <FormBar history={scoreHistory} /> : undefined} sub={t("league.statBoxes.last5Gws")} locked={locked || !m} mobileHide borderColor="var(--color-accent)" />
          <StatBox label={t("league.statBoxes.target.status")} value={statusLabel} sub={t("league.statBoxes.target.goalsCount", { n: m?.totalGoals ?? 0 })} locked={locked || !m} danger={isBust} borderColor="var(--color-accent)" />
        </div>
      );
    }

    const gwLabel = league.activeGameweek?.name ?? (completedGwCount > 0 ? `GW${completedGwCount}` : "");
    const cumulativeSub = gwLabel ? t("league.statBoxes.gwCumulative", { gw: gwLabel }) : t("league.statBoxes.cumulative");

    if (league.mode === "SCORE_PREDICTOR") {
      const spAvgStr = m && completedGwCount > 0 ? (m.score / completedGwCount).toFixed(1) : "—";
      const vsGwAvg = league.leagueGwAverage != null && m ? m.delta - league.leagueGwAverage : null;
      const vsGwAvgNode = vsGwAvg != null
        ? vsGwAvg > 0
          ? <span style={{ color: "var(--color-success)" }}>{`+${vsGwAvg.toFixed(1)}`}</span>
          : vsGwAvg < 0
            ? <span style={{ color: "var(--color-danger)" }}>{vsGwAvg.toFixed(1)}</span>
            : <span>0</span>
        : "—";
      return (
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
          <StatBox label={t("league.statBoxes.score")} value={m?.score ?? 0} sub={cumulativeSub} locked={locked || !m} accent borderColor="var(--color-accent)" />
          <StatBox label={t("league.statBoxes.prevGw")} value={deltaNode} locked={locked || !m} mobileHide borderColor="var(--color-accent)" />
          <StatBox label={t("league.statBoxes.sp.vsGwAvg")} value={vsGwAvgNode} sub={t("league.statBoxes.sp.lastGwVsLeague")} locked={locked || !m || vsGwAvg == null} mobileHide borderColor="var(--color-accent)" />
          <StatBox label={t("league.statBoxes.form")} value={!locked && m ? <FormBar history={scoreHistory} /> : undefined} sub={t("league.statBoxes.last5Gws")} locked={locked || !m} borderColor="var(--color-accent)" />
          <StatBox label={t("league.statBoxes.avgPerGw")} value={spAvgStr} sub={t("league.statBoxes.sp.ptsPerGw")} locked={locked || !m} borderColor="var(--color-accent)" />
        </div>
      );
    }

    // GAMEWEEK_CHALLENGE
    const avgStr = m && completedGwCount > 0 ? (m.score / completedGwCount).toFixed(2) : "—";
    const gwcHits = m?.gwScoreHistory?.filter((h) => h.score > 0).length ?? 0;
    const gwcTotal = m?.gwScoreHistory?.length ?? 0;
    const gwcHitRate = gwcTotal > 0 ? `${Math.round(gwcHits / gwcTotal * 100)}%` : "—";
    return (
      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
        <StatBox label={t("league.statBoxes.score")} value={m?.score ?? 0} sub={cumulativeSub} locked={locked || !m} accent borderColor="var(--color-accent)" />
        <StatBox label={t("league.statBoxes.previousGw")} value={deltaNode} locked={locked || !m} mobileHide borderColor="var(--color-accent)" />
        <StatBox label={t("league.statBoxes.gwc.hitRate")} value={gwcHitRate} sub={t("league.statBoxes.gwc.gwsWithGoal")} locked={locked || !m} borderColor="var(--color-accent)" />
        <StatBox label={t("league.statBoxes.form")} value={!locked && m ? <FormBar history={scoreHistory} /> : undefined} sub={t("league.statBoxes.last5Gws")} locked={locked || !m} borderColor="var(--color-accent)" />
        <StatBox label={t("league.statBoxes.avgPerGw")} value={avgStr} sub={t("league.statBoxes.seasonToDate")} locked={locked || !m} mobileHide borderColor="var(--color-accent)" />
      </div>
    );
  }

  // ─── Leaderboard component ────────────────────────────────────────────────

  const leaderboard = (
    <Leaderboard
      ctx={leaderboardCtx}
      readyMembers={readyMembers}
      approvedMembers={approvedMembers}
      notReadyMembers={notReadyMembers}
      myMember={myMember ?? null}
      onInviteFriends={() => setInviteOpen(true)}
    />
  );



  // ─── Main render ─────────────────────────────────────────────────────────

  const iconBtn: React.CSSProperties = {
    background: "none",
    border: "1px solid var(--color-accent)",
    borderRadius: 8,
    color: "var(--color-accent)",
    cursor: "pointer",
    padding: "7px 10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    fontSize: 12,
    fontFamily: "var(--font-body)",
    position: "relative",
    // 44px minimum touch target (CLAUDE.md) — these header icon buttons are tapped often.
    minHeight: 44,
    minWidth: 44,
  };

  const myTeamLink = league.myMemberId
    ? myTeamHref(league.mode, league.myMemberId, league.id, isStarted)
    : null;

  return (
    <div className="sg-fade-in" style={{ ...widePageContainer, padding: isMobile ? "1rem 0" : "1.5rem 0" }}>

      <div ref={exportFullRef}>

      {/* ── Header card ── */}
      <div ref={exportHeaderRef} style={{ ...card, marginBottom: "1.25rem", padding: isMobile ? "1rem 1.25rem" : "1.5rem 1.75rem" }}>
        {/* Metadata row */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: isMobile ? "0.35rem" : "0.6rem", flexWrap: "wrap" }}>
          <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)" }}>
            {league.competition.name}
          </span>
          <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
            <ModeIconSmall mode={league.mode} color={MODE_COLOR_VAR[league.mode]} />
            <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: MODE_COLOR_VAR[league.mode] }}>
              {t(`${MODE_CONTENT[league.mode].keyRoot}.label`)}
            </span>
          </span>
          <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
          <span style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--color-text-dim)" }}>
            {league.competition.season}
          </span>
          <span style={{ color: "var(--color-border)", fontSize: "0.75rem" }}>·</span>
          <StatusDot status={league.status} />
        </div>

        {/* Title + action buttons */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap", marginBottom: isMobile ? "0.75rem" : "1.25rem" }}>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ margin: "0 0 0.3rem", fontFamily: "var(--font-display)", fontSize: isMobile ? "1.4rem" : "2rem", fontWeight: 800, letterSpacing: "0.01em", color: "var(--color-text)", lineHeight: 1.1 }}>
              {league.name}
            </h1>
            <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-muted)" }}>
              <span style={{ color: "var(--color-accent)" }}>{readyMembers.length || approvedMembers.length}</span> {t("league.header.membersSuffix")}
              {league.activeGameweek && <> · {t("league.header.gameweekLabel")} <span style={{ color: "var(--color-accent)" }}>{t("league.header.ofTotal", { current: league.activeGameweek.number, total: totalGwCount })}</span></>}
              {league.startGameweek && <> · {isStarted ? t("league.header.startedIn") : t("league.header.startsIn")} <span style={{ color: "var(--color-accent)" }}>{league.startGameweek.name}</span></>}
            </p>
          </div>
          <div data-no-export style={{ display: "flex", gap: "0.35rem", flexShrink: 0, alignItems: "center" }}>
            <button onClick={() => setInfoOpen(true)} style={iconBtn} title={t("league.header.infoTitle")}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 7v5M8 5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
            {league.gwSummary && (
              <button onClick={() => setGwRecapOpen(true)} style={iconBtn} title={t("gwRecap.reopen")}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M2 8a6 6 0 1 1 1.76 4.24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M2 4v3.5H5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 5v3l2 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            {effectiveAdmin && league.status === "PREPARING" && (
              <button onClick={() => setSettingsOpen(true)} style={iconBtn} title={t("league.settingsModal.title")}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M8 10a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M13.3 6.6l-.6-1.4 1-1-1.9-1.9-1 1-1.4-.6V1H7.6v1.7l-1.4.6-1-1L3.3 4.2l1 1-.6 1.4H2v1.8h1.7l.6 1.4-1 1 1.9 1.9 1-1 1.4.6V15h1.8v-1.7l1.4-.6 1 1 1.9-1.9-1-1 .6-1.4H15V6.6h-1.7z" stroke="currentColor" strokeWidth="1.3"/>
                </svg>
              </button>
            )}
            {league.status === "PREPARING" && (
              <button onClick={() => setInviteOpen(true)} style={iconBtn} title={t("league.inviteModal.button")}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M11 7.5a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0zM13.5 14c0-2.485-2.462-4.5-5.5-4.5S2.5 11.515 2.5 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M13 4V7M14.5 5.5H11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            )}
            <div style={{ position: "relative" }}>
                <button style={{ ...iconBtn }} onClick={() => setShareDropdownOpen((v) => !v)} disabled={shareLoading} title={t("league.header.exportBtn")}>
                  {shareLoading
                    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="12"/></svg>
                    : <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2v11M6 9l4 4 4-4"/><path d="M3 14v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"/></svg>
                  }
                </button>
                {shareDropdownOpen && isMobile && (
                  <>
                    <div style={{ position: "fixed", inset: 0, zIndex: 199, background: "rgba(0,0,0,0.45)" }} onClick={() => setShareDropdownOpen(false)} />
                    <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200, background: "var(--color-surface)", borderRadius: "16px 16px 0 0", padding: "1.25rem 1.25rem calc(1.25rem + env(safe-area-inset-bottom))", boxShadow: "0 -8px 32px var(--color-shadow)" }}>
                      <div style={{ width: 36, height: 4, borderRadius: 2, background: "var(--color-border)", margin: "0 auto 1rem" }} />
                      <div style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)", marginBottom: "0.4rem" }}>{t("league.shareMenu.simple")}</div>
                      {(["download", "copy"] as const).map((action) => (
                        <button key={action} onClick={() => handleShare("simple", action)} style={{ display: "block", width: "100%", padding: "0.75rem 0", background: "none", border: "none", textAlign: "left", fontSize: "1rem", color: "var(--color-text)", cursor: "pointer", borderBottom: "1px solid var(--color-border-subtle)" }}>
                          {action === "download" ? t("league.shareMenu.download") : t("league.shareMenu.copy")}
                        </button>
                      ))}
                      <div style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)", margin: "0.75rem 0 0.4rem" }}>{t("league.shareMenu.full")}</div>
                      {(["download", "copy"] as const).map((action) => (
                        <button key={action} onClick={() => handleShare("full", action)} style={{ display: "block", width: "100%", padding: "0.75rem 0", background: "none", border: "none", textAlign: "left", fontSize: "1rem", color: "var(--color-text)", cursor: "pointer", borderBottom: "1px solid var(--color-border-subtle)" }}>
                          {action === "download" ? t("league.shareMenu.download") : t("league.shareMenu.copy")}
                        </button>
                      ))}
                      {league.status === "PREPARING" && (
                        <>
                          <div style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)", margin: "0.75rem 0 0.4rem" }}>{t("league.shareMenu.withInvite")}</div>
                          {(["download", "copy"] as const).map((action) => (
                            <button key={action} onClick={() => handleShare("simple", action, true)} style={{ display: "block", width: "100%", padding: "0.75rem 0", background: "none", border: "none", textAlign: "left", fontSize: "1rem", color: "var(--color-text)", cursor: "pointer", borderBottom: action === "copy" ? "none" : "1px solid var(--color-border-subtle)" }}>
                              {action === "download" ? t("league.shareMenu.download") : t("league.shareMenu.copy")}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </>
                )}
                {shareDropdownOpen && !isMobile && (
                  <>
                    <div style={{ position: "fixed", inset: 0, zIndex: 99 }} onClick={() => setShareDropdownOpen(false)} />
                    <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 10, boxShadow: "0 8px 32px var(--color-shadow)", zIndex: 100, minWidth: 190, overflow: "hidden" }}>
                      <div style={{ padding: "0.35rem 1rem 0.2rem", fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)" }}>{t("league.shareMenu.simple")}</div>
                      {(["download", "copy"] as const).map((action) => (
                        <button key={action} onClick={() => handleShare("simple", action)} style={{ display: "block", width: "100%", padding: "0.5rem 1rem", background: "none", border: "none", textAlign: "left", fontSize: "0.85rem", color: "var(--color-text)", cursor: "pointer" }}>
                          {action === "download" ? t("league.shareMenu.download") : t("league.shareMenu.copy")}
                        </button>
                      ))}
                      <div style={{ margin: "0.2rem 1rem", borderTop: "1px solid var(--color-border-subtle)" }} />
                      <div style={{ padding: "0.35rem 1rem 0.2rem", fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)" }}>{t("league.shareMenu.full")}</div>
                      {(["download", "copy"] as const).map((action) => (
                        <button key={action} onClick={() => handleShare("full", action)} style={{ display: "block", width: "100%", padding: "0.5rem 1rem", background: "none", border: "none", textAlign: "left", fontSize: "0.85rem", color: "var(--color-text)", cursor: "pointer" }}>
                          {action === "download" ? t("league.shareMenu.download") : t("league.shareMenu.copy")}
                        </button>
                      ))}
                      {league.status === "PREPARING" && (
                        <>
                          <div style={{ margin: "0.2rem 1rem", borderTop: "1px solid var(--color-border-subtle)" }} />
                          <div style={{ padding: "0.35rem 1rem 0.2rem", fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-text-dim)" }}>{t("league.shareMenu.withInvite")}</div>
                          {(["download", "copy"] as const).map((action) => (
                            <button key={action} onClick={() => handleShare("simple", action, true)} style={{ display: "block", width: "100%", padding: "0.5rem 1rem", background: "none", border: "none", textAlign: "left", fontSize: "0.85rem", color: "var(--color-text)", cursor: "pointer" }}>
                              {action === "download" ? t("league.shareMenu.download") : t("league.shareMenu.copy")}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                  </>
                )}
                {exportStatus && (
                  <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 100, whiteSpace: "nowrap", padding: "0.4rem 0.7rem", borderRadius: 8, fontSize: "0.78rem", fontWeight: 600, background: "var(--color-surface)", border: `1px solid ${exportStatus.type === "error" ? "var(--color-danger)" : "var(--color-border)"}`, color: exportStatus.type === "error" ? "var(--color-danger)" : "var(--color-text)", boxShadow: "0 8px 32px var(--color-shadow)" }}>
                    {exportStatus.message}
                  </div>
                )}
            </div>
            {myTeamLink && (
              <Link to={myTeamLink} style={{ ...primaryBtn, textDecoration: "none", fontSize: "0.85rem" }}>
                {league.mode === "FINAL_POSITIONS"
                  ? t("league.myPredictions")
                  : league.mode === "GAMEWEEK_CHALLENGE"
                    ? t("league.header.ctaSelections")
                    : league.mode === "SCORE_PREDICTOR"
                      ? t("league.myPredictions")
                      : league.mode === "SYNDICATE"
                        ? (isStarted ? t("syndicate.viewMyPicks") : t("syndicate.pickMyPicks"))
                        : league.status === "PREPARING"
                          ? (myMember?.isTeamValid ? t("league.header.ctaTeamEdit") : t("league.selectPlayers"))
                          : t("league.header.ctaTeamView")}
              </Link>
            )}
          </div>
        </div>

        <LeagueProgressBar
          totalGws={totalGwCount}
          currentGwNumber={currentGwNumber}
          startGwNumber={league.startGameweek?.number ?? 1}
          status={league.status}
        />

        {renderStatBoxes()}
      </div>

      {/* League created — one-off invite code nudge */}
      {showCreatedBanner && (
        <div data-no-export style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap",
          background: "color-mix(in srgb, var(--color-warning) 12%, transparent)", border: "1px solid var(--color-warning)", borderRadius: 8,
          padding: "0.75rem 1.25rem", marginBottom: "1.25rem", fontSize: "0.85rem", color: "var(--color-warning)", fontWeight: 500,
        }}>
          <span>
            {t("league.header.createdBanner")}{" "}
            <code style={{ background: "var(--color-surface-alt)", border: "1px solid var(--color-border)", borderRadius: 6, padding: "2px 8px", fontWeight: 700, letterSpacing: "0.05em" }}>{league.inviteCode}</code>
            {" "}
            <button onClick={handleCopy} style={{ ...secondaryBtn, fontSize: "0.72rem", padding: "0.25rem 0.6rem" }}>
              {copied ? t("league.copied") : t("league.copy")}
            </button>
            {" "}
            {/* Intent peaks right after creating a league — UK mini-leagues spread on
                WhatsApp more than anywhere else, so this is the moment to offer it directly
                rather than making the admin go find the share menu. */}
            <a
              href={`https://wa.me/?text=${encodeURIComponent(t("league.header.whatsappMessage", { name: league.name, url: buildJoinUrl(league.inviteCode) }))}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...secondaryBtn, fontSize: "0.72rem", padding: "0.25rem 0.6rem", textDecoration: "none", display: "inline-block" }}
            >
              {t("league.header.whatsappInvite")}
            </a>
          </span>
          <button onClick={() => setShowCreatedBanner(false)} aria-label={t("league.header.dismiss")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-muted)", fontSize: "1.1rem", lineHeight: 1, padding: "0 4px", flexShrink: 0 }}>×</button>
        </div>
      )}

      {/* Banners */}
      <div data-no-export>
      {(() => {
        const banner = (color: string, content: React.ReactNode) => (
          <div style={{ background: `color-mix(in srgb, ${color} 12%, transparent)`, border: `1px solid ${color}`, borderRadius: 8, padding: "0.75rem 1.25rem", marginBottom: "1.25rem", fontSize: "0.85rem", color, fontWeight: 500 }}>
            {content}
          </div>
        );
        return (
          <>
            {league.status === "COMPLETED" && banner("var(--color-success)", t("league.banners.completed"))}
            {league.mode === "GAMEWEEK_CHALLENGE" && league.gwcNextPickMissing && myTeamLink && banner("var(--color-warning)", <>{t("league.banners.gwcMissingPick")} <Link to={myTeamLink} style={{ color: "inherit", fontWeight: 700 }}>{t("league.banners.gwcMissingPickCta")}</Link></>)}
            {league.mode === "SCORE_PREDICTOR" && league.spNextGwPredictionsMissing && myTeamLink && banner("var(--color-warning)", <>{t("league.banners.spMissingPrediction")} <Link to={myTeamLink} style={{ color: "inherit", fontWeight: 700 }}>{t("league.banners.spMissingPredictionCta")}</Link></>)}
            {league.mode === "SYNDICATE" && !isStarted && myTeamLink && !myMember?.isReady && banner("var(--color-warning)", <><Link to={myTeamLink} style={{ color: "inherit", fontWeight: 700 }}>{t("syndicate.pickMyPicks")}</Link> — {t("syndicate.picksLockOnStart")}</>)}
          </>
        );
      })()}
      </div>

      {/* ── Dashboard grid ── */}
      {isMobile && !isStarted && (
          <div style={{ marginBottom: "1.25rem" }}>
            <HowItWorksPanel mode={league.mode} actionLink={myTeamLink} onViewRules={() => setInfoOpen(true)} onInviteFriends={() => setInviteOpen(true)} />
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 320px", gap: "1.25rem", alignItems: "start" }}>

          {/* Leaderboard + Trophies (left column) */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            <section ref={exportLeaderboardRef} style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "1.1rem 1.25rem 0.4rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <h2 style={{ ...panelHeading, margin: 0 }}>
                  {!isStarted ? t("league.leaderboardHeader.members") : league.status === "COMPLETED" ? t("league.leaderboardHeader.final") : t("league.leaderboardHeader.gw", { n: completedGwCount })}
                </h2>
              </div>
              {leaderboard}
            </section>
          </div>

          {/* Right panel */}
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {!isMobile && !isStarted && (
              <HowItWorksPanel mode={league.mode} actionLink={myTeamLink} onViewRules={() => setInfoOpen(true)} onInviteFriends={() => setInviteOpen(true)} />
            )}
            {league.mode === "GAMEWEEK_CHALLENGE" ? (
              <GwScorersPanel user={user} leagueId={league.id} resultsGwId={resultsGwId} />
            ) : league.mode === "SYNDICATE" ? (
              <>
                <FixturesPanel user={user} leagueId={league.id} resultsGwId={resultsGwId} nextGwId={nextGwId} leagueStatus={league.status} highlightedClubIds={myHighlightedClubIds} leagueMode={league.mode} />
                <SyndicateGwStatsPanel user={user} leagueId={league.id} resultsGwId={resultsGwId} highlightedPlayerIds={myHighlightedPlayerIds} />
              </>
            ) : (
              <FixturesPanel user={user} leagueId={league.id} resultsGwId={resultsGwId} nextGwId={nextGwId} leagueStatus={league.status} highlightedClubIds={myHighlightedClubIds} leagueMode={league.mode} />
            )}
            <GwSummaryPanel gwSummary={league.gwSummary} members={league.members} isStarted={isStarted} />
            <TrophiesPanel trophies={isStarted ? trophies : undefined} myName={myMember?.displayName} />
            {league.mode === "GAMEWEEK_CHALLENGE" && !isStarted ? (
              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                <div style={{ padding: "1.1rem 1.25rem 0.4rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <h2 style={{ ...panelHeading, margin: 0 }}>My Selections</h2>
                  {gwcPickRows && <span style={{ fontSize: "0.72rem", color: "var(--color-text-muted)" }}>{gwcPickRows.filter((r) => r.pick).length} of {gwcPickRows.length} selected</span>}
                </div>
                {gwcPickRows === null ? (
                  <div style={{ padding: "1rem 1.25rem" }}>
                    <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>Loading…</p>
                  </div>
                ) : gwcPickRows.length === 0 ? (
                  <div style={{ padding: "1rem 1.25rem" }}>
                    <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>No gameweeks yet.</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: 400, overflowY: "auto" }}>
                    {gwcPickRows.map((row, idx) => {
                      const cfg = row.pick ? getClubBadge(row.pick.player.club) : null;
                      return (
                        <div
                          key={row.gameweekId}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "44px 1fr",
                            alignItems: "center",
                            gap: "0.75rem",
                            padding: "0.65rem 1rem",
                            borderBottom: idx < gwcPickRows.length - 1 ? "1px solid var(--color-border-subtle)" : "none",
                            background: !row.pick ? "color-mix(in srgb, var(--color-warning) 6%, transparent)" : "transparent",
                          }}
                        >
                          <div style={{ fontFamily: "var(--font-display)", fontSize: "0.95rem", fontWeight: 800, color: "var(--color-text-dim)", lineHeight: 1 }}>
                            {row.gameweekName}
                          </div>
                          <div>
                            {row.pick ? (
                              <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                {cfg && <ClubBadge config={cfg} size={16} title={row.pick.player.club} />}
                                <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.pick.player.name}</span>
                              </div>
                            ) : (
                              <span style={{ fontSize: "0.75rem", color: "var(--color-warning)", fontWeight: 500 }}>No selection</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : league.mode === "TARGET" && !isStarted ? (
              (() => {
                const slots = 4;
                const selections = myMember?.selections ?? [];
                const posOrder: Record<string, number> = { GK: 0, DF: 1, MF: 2, FW: 3 };
                const sorted = [...selections].sort((a, b) => (posOrder[a.player.position ?? ""] ?? 9) - (posOrder[b.player.position ?? ""] ?? 9));
                const empty = slots - sorted.length;
                return (
                  <div style={{ background: "var(--color-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 12, overflow: "hidden" }}>
                    <div style={{ padding: "1.1rem 1.25rem 0.4rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <h2 style={{ ...panelHeading, margin: 0 }}>My Team</h2>
                      <span style={{ fontSize: "0.72rem", color: "var(--color-text-muted)" }}>{sorted.length} of {slots} selected</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                      {sorted.map((sel, i) => {
                        const { player } = sel;
                        const flagUrl = player.nationality ? getFlagUrl(player.nationality) : null;
                        const clubCfg = player.club ? getClubBadge(player.club) : null;
                        const posColors: Record<string, string> = { GK: "oklch(0.75 0.18 60)", DF: "oklch(0.65 0.18 145)", MF: "oklch(0.65 0.18 260)", FW: "oklch(0.65 0.18 30)" };
                        const posColor = posColors[player.position ?? ""] ?? "var(--color-border)";
                        return (
                          <div key={player.id} style={{ display: "flex", alignItems: "center", gap: "0.65rem", padding: "0.6rem 1rem", borderBottom: (i < sorted.length - 1 || empty > 0) ? "1px solid var(--color-border-subtle)" : "none", borderLeft: `3px solid ${posColor}` }}>
                            {clubCfg && <ClubBadge config={clubCfg} size={22} title={player.club ?? ""} />}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{player.name}</div>
                              <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", marginTop: "0.15rem" }}>
                                {flagUrl && <img src={flagUrl} alt={player.nationality ?? ""} title={player.nationality ?? ""} style={{ width: 14, height: "auto", borderRadius: 2, display: "block" }} />}
                                {player.club && <span style={{ fontSize: "0.72rem", color: "var(--color-text-dim)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{player.club}</span>}
                              </div>
                            </div>
                            {player.position && (
                              <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", padding: "2px 5px", borderRadius: 4, background: "var(--color-surface-alt)", color: "var(--color-text-muted)", border: "1px solid var(--color-border-subtle)", flexShrink: 0 }}>
                                {player.position}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {Array.from({ length: empty }).map((_, i) => (
                        <div key={`empty-${i}`} style={{ display: "flex", alignItems: "center", padding: "0.6rem 1rem", borderBottom: i < empty - 1 ? "1px solid var(--color-border-subtle)" : "none", borderLeft: "3px solid var(--color-border-subtle)", opacity: 0.45 }}>
                          <span style={{ fontSize: "0.82rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>Empty slot</span>
                        </div>
                      ))}
                      {sorted.length === 0 && empty === 0 && (
                        <div style={{ padding: "1rem 1.25rem" }}>
                          <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>You have not selected any players yet.</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            ) : league.mode === "FINAL_POSITIONS" && !isStarted ? (
              <div style={{ background: "var(--color-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 12, padding: "1rem 1.25rem" }}>
                <h2 style={{ ...panelHeading, marginBottom: "0.75rem" }}>Your Predictions</h2>
                {league.myPrediction && league.myPrediction.length > 0 ? (
                  <div>
                    {(() => {
                      const sorted = [...league.myPrediction].sort((a, b) => a.position - b.position);
                      const cfg = league.fpScoringConfig;
                      const bands = computePositionBands({
                        clubCount: sorted.length,
                        topTierSize: cfg?.topTierSize ?? null,
                        relegationSize: cfg?.relegationSize ?? null,
                        championEnabled: cfg?.championEnabled ?? true,
                        topTierLabel: cfg?.topTierLabel ?? null,
                        relegationLabel: cfg?.relegationLabel ?? null,
                        fallbackLabels: {
                          champion: t("prediction.bands.champion"),
                          top: t("prediction.bands.top"),
                          mid: t("prediction.bands.mid"),
                          relegation: t("prediction.bands.relegation"),
                        },
                      });
                      return sorted.map((pred, i, arr) => {
                        const club = league.fpClubs?.find((c) => c.id === pred.clubId);
                        const band = bands[i];
                        const accent = fpBandAccent(band);
                        return (
                          <div key={pred.id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.3rem 0.5rem", borderBottom: i < arr.length - 1 ? "1px solid var(--color-border-subtle)" : "none", borderLeft: `3px solid ${accent.color}` }}>
                            <span style={{ fontFamily: "var(--font-display)", fontSize: "0.85rem", fontWeight: 700, color: "var(--color-text-dim)", width: "1.2rem", flexShrink: 0, textAlign: "right" }}>
                              {pred.position}
                            </span>
                            <span style={{ flex: 1, minWidth: 0, fontSize: "0.82rem", color: "var(--color-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {club?.shortName ?? club?.name ?? "—"}
                            </span>
                            <FPBandPill band={band} />
                          </div>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <p style={{ margin: 0, fontSize: "0.82rem", color: "var(--color-text-dim)", fontStyle: "italic" }}>
                    You have not made predictions yet.
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </div>

      </div>{/* end exportFullRef */}

      {/* ── How to play modal ── */}
      {infoOpen && (() => {
        const { rules } = league;
        const adminMember = league.members.find((m) => m.userId === league.adminId);
        const cfg = league.fpScoringConfig;
        const leagueFacts: LeagueFact[] = [];
        if (adminMember) leagueFacts.push({ label: t("league.rules.admin"), value: adminMember.displayName });
        if (rules.mode === "TARGET") {
          leagueFacts.push({ label: t("league.rules.target"), value: rules.target });
        }
        if (rules.mode === "FINAL_POSITIONS") {
          if (cfg?.championEnabled !== false) {
            leagueFacts.push({ label: t("league.rules.championBonus"), value: t("league.rules.championBonusValue") });
          }
          if (cfg?.topTierSize != null && cfg.topTierSize > 0) {
            leagueFacts.push({ label: t("league.rules.setBonus", { label: cfg.topTierLabel?.trim() || `Top-${cfg.topTierSize}` }), value: t("league.rules.setBonusValue") });
          }
          if (cfg?.relegationSize != null && cfg.relegationSize > 0) {
            leagueFacts.push({ label: t("league.rules.setBonus", { label: cfg.relegationLabel?.trim() || `Relegation-${cfg.relegationSize}` }), value: t("league.rules.setBonusValue") });
          }
        }
        return <HowToPlayModal mode={rules.mode} leagueFacts={leagueFacts} onClose={() => setInfoOpen(false)} />;
      })()}

      {/* ── Gameweek recap modal ── */}
      {gwRecapOpen && league.myMemberId && (
        <GameweekRecapModal
          user={user}
          leagueId={league.id}
          mode={league.mode}
          myMemberId={league.myMemberId}
          members={readyMembers}
          tournamentGoals={tiebreakerGoalsToDate}
          onClose={() => setGwRecapOpen(false)}
        />
      )}

      {/* ── Settings modal (admin only) ── */}
      {settingsOpen && effectiveAdmin && (
        <Modal onClose={() => setSettingsOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--color-surface)", borderRadius: 16, border: "1px solid color-mix(in srgb, var(--color-accent) 25%, var(--color-border))", boxShadow: "0 0 0 1px color-mix(in srgb, var(--color-accent) 12%, transparent), 0 24px 64px var(--color-shadow)", padding: "1.5rem", maxWidth: 460, width: "90%", maxHeight: "85vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700 }}>{t("league.settingsModal.title")}</h3>
              <button onClick={() => setSettingsOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-muted)", fontSize: "1.2rem", lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>

            {/* Danger zone */}
            {league.status === "PREPARING" && (
              <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: "1rem" }}>
                <p style={{ margin: "0 0 0.6rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-danger)" }}>
                  {t("league.settingsModal.dangerZone")}
                </p>
                <button style={dangerBtn} onClick={() => { setSettingsOpen(false); setConfirmDelete(true); }}>
                  {t("league.deleteLeague")}
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* ── Invite Friends modal ── */}
      {inviteOpen && (
        <Modal onClose={() => setInviteOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--color-surface)", borderRadius: 16, border: "1px solid color-mix(in srgb, var(--color-accent) 25%, var(--color-border))", boxShadow: "0 0 0 1px color-mix(in srgb, var(--color-accent) 12%, transparent), 0 24px 64px var(--color-shadow)", padding: "1.5rem", maxWidth: 420, width: "90%", maxHeight: "85vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 700 }}>{t("league.inviteModal.title")}</h3>
              <button onClick={() => setInviteOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-muted)", fontSize: "1.2rem", lineHeight: 1, padding: "0 4px" }}>×</button>
            </div>

            {/* Invite code */}
            <div>
              <p style={{ margin: "0 0 0.5rem", fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)" }}>
                {t("league.inviteModal.codeLabel")}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <code style={{ flex: 1, background: "var(--color-surface-alt)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: "0.6rem 0.85rem", fontSize: "1rem", fontFamily: "var(--font-mono)", letterSpacing: "0.1em", color: "var(--color-text)" }}>
                  {league.inviteCode}
                </code>
                <button style={secondaryBtn} onClick={handleCopy}>
                  {copied ? t("league.copied") : t("league.copy")}
                </button>
              </div>
            </div>

            {/* Add Guest — admin only, PREPARING phase, not GWC */}
            {effectiveAdmin && league.status === "PREPARING" && league.mode !== "GAMEWEEK_CHALLENGE" && (
              <div style={{ borderTop: "1px solid var(--color-border)", paddingTop: "1rem" }}>
                <p style={{ margin: "0 0 0.5rem", fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)" }}>
                  {t("league.addGuestEntry")}
                </p>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    placeholder={t("addGuest.displayNamePlaceholder")}
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddGuest()}
                    maxLength={50}
                  />
                  <button style={primaryBtn} onClick={handleAddGuest} disabled={addingGuest || guestName.trim().length < 2}>
                    {addingGuest ? t("common.saving") : t("common.add")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Delete league confirm */}
      {confirmDelete && (
        <ConfirmModal
          title={t("league.confirmDelete.title")}
          message={t("league.confirmDelete.message", { name: league.name })}
          confirmLabel={t("common.delete")}
          confirming={actionLoading}
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(false)}
        />
      )}


    </div>
  );
}
